import {
  type ComponentType,
  type SVGProps,
  Suspense,
  lazy as reactLazy,
} from 'react';
import { type IconName as MarketIconName, iconNames as marketIconNames } from '@market/icons/icon-names';

export type PreloadableComponent = ComponentType<SVGProps<SVGSVGElement> & { title?: string; slot?: string }> & {
  preload: () => Promise<ComponentType>;
};

function lazy(
  factory: () => Promise<{ default: ComponentType }>,
): PreloadableComponent {
  const LazyComponent = reactLazy(factory);
  let factoryPromise: Promise<ComponentType> | undefined;

  const preload = () => {
    // Only preload an icon once
    if (!factoryPromise) {
      factoryPromise = factory().then((module) => module.default);
    }
    return factoryPromise;
  };

  return Object.assign(LazyComponent, { preload });
}

type IconComponentConfig = Record<string, PreloadableComponent>;

const IconPaths: IconComponentConfig = {};

// Add custom icons
const customIconNames = [
  'american-express-bw',
  'bank-multicolor',
  'barcode-scanner',
  'can',
  'cash-drawer',
  'empty',
  'fdic',
  'google',
  'handheld',
  'internal-barcode-scanner',
  'internal-reader',
  'kitchen-display',
  'magstripe-reader',
  'migration',
  'phone-with-background',
  'point-of-sale',
  'printer',
  'reader',
  'register',
  'scale-with-background',
  'square-ai-fill',
  'stand',
  'stand-transparent',
  'tablet-with-background',
  'tap-to-pay',
  'terminal',
] as const;

for (const name of customIconNames) {
  IconPaths[name] = lazy(() => import(`./icons/${name}.svg?react`));
}

// Add market icons
const marketIcons = import.meta.glob<{
  default: ComponentType;
}>('../../../node_modules/@market/icons/assets/svg/*.svg', { eager: false, query: '?react' });

for (const path in marketIcons) {
  const match = path.match(/\/([^/]+)\.svg$/);
  if (match) {
    const iconName = match[1];
    IconPaths[iconName] = lazy(marketIcons[path]);
  }
}

type CustomIconName = (typeof customIconNames)[number];

export type IconName = MarketIconName | CustomIconName;

export const IconNames = [...marketIconNames, ...customIconNames] as IconName[];

// Transparent icons that don't end in '-multicolor'
const TransparentIconsSet = new Set([
  'american-express-bw',
  'fdic',
  'migration',
  'register',
  'tablet-with-background',
  'terminal',
]);

const isIconTransparent = (iconName: string) => iconName.endsWith('-multicolor') || TransparentIconsSet.has(iconName);

export interface Props {
  'aria-hidden'?: boolean;
  fill?: string | 'transparent';
  iconName: IconName;
  size?: string | number;
  display?: 'block' | 'inline';
  slot?: string;
  title?: string;
}
export const Icon = ({
  'aria-hidden': ariaHidden,
  iconName,
  size = '',
  fill = 'var(--core-text-10-color)',
  display = 'inline',
  slot = '',
  title,
}: Props) => {
  const Icon = (IconPaths as IconComponentConfig)[iconName];
  const hasFill = fill !== 'transparent' && !isIconTransparent(iconName);
  const uniqueId = `icon-${iconName}-${Math.random().toString(36).slice(2, 11)}`;

  return (
    <Suspense>
      {/* Apply styles inline so we don't have to import Icon styles everywhere this component is used */}
      {hasFill && (
        <style>
          {`.${uniqueId} * { fill: ${fill} !important; }`}
        </style>
      )}
      <Icon
        aria-hidden={ariaHidden ?? false}
        className={hasFill ? uniqueId : ''}
        role="img"
        slot={slot}
        style={{
          display,
          width: size,
          height: size,
        }}
        title={title ?? iconName}
      />
    </Suspense>
  );
};

/**
 * Preloads the icons for the given icon names.
 * @param iconNames - Icon names to preload.
 * @returns A promise that resolves when all the icons are preloaded.
 * @example
 * preloadIcons(['binoculars']);
 */
export function preloadIcons(iconNames: IconName[]) {
  iconNames.forEach((iconName) => {
    const Icon = (IconPaths as IconComponentConfig)[iconName];
    Icon.preload();
  });
}
